// B10 S17 · B1's Action Center read, called through the tool door (CR-092 "Prioritized actions across reqs,
// candidates, scheduling, packets, scorecards, panels, approvals, offers and closeout. Deep link to exact screen; close
// automatically when resolved; retain history."; plan D11). `action_center.list` is B1's tool: the generated
// `actionCenterList` and its input and output types; the shaping below (groups, row props, links) is the screen's.

import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import type { AppError } from "@/api/runtime";
import {
  actionCenterList,
  type ActionCenterListInput,
  type ActionCenterListOutput,
  type ActionRow,
} from "@/api/tools";
import type { ActionKind, ActionRowProps, DueTier, Settle } from "@/mc";
import { hsKey } from "@/views/b10/lib/hsKey";

export { actionCenterList };
export type { ActionCenterListInput, ActionCenterListOutput };

export const ACTION_CENTER_LIST = "action_center.list";

/** B1 `ActionRow`. Datetimes arrive as ISO strings. `requisition_title` is set only where the reader may open the requisition. */
export type ActionCenterRow = ActionRow;
export type ActionTab = NonNullable<ActionCenterListInput["tab"]>;
/** B1's tiers (`shell_vocab.ACTION_TIERS`), most severe first; `blocked` sorts last. */
export type ActionTier = NonNullable<ActionCenterListInput["tiers"]>[number];
/** B1's due windows (business calendar): passed due, due by the end of today, due within seven days. */
export type DueWindow = NonNullable<ActionCenterListInput["due"]>;
export type ActionSort = NonNullable<ActionCenterListInput["sort"]>;

/** The nine kinds in his order (BRD 4.1; B1 `spine.vocab.ACTION_CATEGORIES`). */
export const ACTION_KINDS: readonly ActionKind[] = [
  "reqs", "candidates", "scheduling", "packets", "scorecards", "panels", "approvals", "offers", "closeout",
];
/** The tier groups in his order (DC-45 *Breach — dashboard risk · Near-breach · Reminder · Due*), B1's fifth last. */
export const ACTION_TIERS: readonly ActionTier[] = ["breach", "near_breach", "reminder", "due", "blocked"];
export const DUE_WINDOWS: readonly DueWindow[] = ["overdue", "today", "week"];
/** DC-45 *Priority · Due · Requisition*. */
export const ACTION_SORTS: readonly ActionSort[] = ["priority", "due", "requisition"];

/** One open page: B1's largest. The open tab reads every page B1 resolves (it stops at 1000 open rows, `OPEN_CAP`). */
export const OPEN_PAGE = 200;
export const OPEN_PAGES = 5;
/** One History page; the next loads when the end of the list comes into view. */
export const HISTORY_PAGE = 50;

/** One query's input: the tab, sort and page size always given (B1 `ListInput`'s defaults are not the screen's). */
export type ActionPagesInput = Omit<ActionCenterListInput, "cursor" | "tab" | "sort" | "limit"> & {
  tab: ActionTab;
  sort: ActionSort;
  limit: number;
};
export type ActionPages = InfiniteData<ActionCenterListOutput, string | undefined>;

export function actionPagesKey(input: ActionPagesInput): readonly unknown[] {
  return [ACTION_CENTER_LIST, input];
}

/**
 * The pages of one query. Never stale: coming back from the exact screen re-reads at once, so a row the action resolved
 * leaves the open list (DC-47's silent collapse) and appears under History. Each call registers with the capture settle
 * signal, so a frame waits for it.
 */
export function useActionPages(input: ActionPagesInput, settle: Settle) {
  return useInfiniteQuery<ActionCenterListOutput, AppError, ActionPages, readonly unknown[], string | undefined>({
    queryKey: actionPagesKey(input),
    queryFn: ({ pageParam }) => {
      const call = actionCenterList(pageParam === undefined ? input : { ...input, cursor: pageParam });
      settle.pending(call);
      return call;
    },
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: false,
    retryOnMount: false,
    staleTime: 0,
    refetchOnWindowFocus: input.tab === "open",
  });
}

/** Every row of the pages read so far, once each, in the server's order. */
export function rowsOf(data: ActionPages | undefined): ActionCenterRow[] {
  const seen = new Set<string>();
  const out: ActionCenterRow[] = [];
  for (const page of data?.pages ?? []) {
    for (const row of page.items) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
  }
  return out;
}

/** The counts of the latest page read (each page carries the whole tally). */
export function countsOf(data: ActionPages | undefined): Readonly<Record<string, number>> | undefined {
  return data?.pages.at(-1)?.counts;
}

/**
 * The *Prioritized actions* tab's count for a filter, so the count agrees with the rows behind it. B1's `open` counts the
 * open rows the due and requisition filters keep, before the kind and tier filters (its `history` counts after them), so
 * on *Approvals* it would count every kind. With kinds chosen the count is the sum of their `kind:` tallies, with tiers
 * chosen the sum of their `tier:` tallies; with both, no tally crosses them, so it is the rows listed once every page is
 * read (`listed`), else absent.
 */
export function openCountOf(
  counts: Readonly<Record<string, number>> | undefined,
  filter: { kinds: readonly ActionKind[]; tiers: readonly ActionTier[] },
  listed?: number,
): number | undefined {
  if (!counts) return undefined;
  const sum = (keys: readonly string[]) => keys.reduce((total, key) => total + (counts[key] ?? 0), 0);
  if (filter.kinds.length && filter.tiers.length) return listed;
  if (filter.kinds.length) return sum(filter.kinds.map((kind) => `kind:${kind}`));
  if (filter.tiers.length) return sum(filter.tiers.map((tier) => `tier:${tier}`));
  return counts["open"];
}

export function tierOf(row: Pick<ActionCenterRow, "tier">): ActionTier {
  return ACTION_TIERS.find((tier) => tier === row.tier) ?? "due";
}

/** B1 groups a subject it cannot classify under `reqs`; a category this screen does not know is drawn the same way. */
export function kindOf(row: Pick<ActionCenterRow, "category">): ActionKind {
  return ACTION_KINDS.find((kind) => kind === row.category) ?? "reqs";
}

export interface TierGroup {
  tier: ActionTier;
  rows: ActionCenterRow[];
}

/** The open rows under their tier, in his order (DC-45); a tier with no row has no group; the server's order holds inside one. */
export function groupByTier(rows: readonly ActionCenterRow[]): TierGroup[] {
  return ACTION_TIERS
    .map((tier) => ({ tier, rows: rows.filter((row) => tierOf(row) === tier) }))
    .filter((group) => group.rows.length > 0);
}

/** DC-19: breach is *Overdue* (*Past Due* for scorecards), then *Near-breach*, *Reminder*, and the due date. */
export function dueTierOf(row: Pick<ActionCenterRow, "tier" | "category">): DueTier {
  switch (tierOf(row)) {
    case "breach":
      return kindOf(row) === "scorecards" ? "pastDue" : "overdue";
    case "near_breach":
      return "nearBreach";
    case "reminder":
      return "reminder";
    default:
      return "due";
  }
}

/** A route as a hash link. */
export function hashOf(route: string): string {
  return route.startsWith("#") ? route : `#${route}`;
}

/** The exact screen of an open row (B1 gives every item an application route); without one, its requisition. */
export function deepLinkOf(row: Pick<ActionCenterRow, "route" | "requisition_id">): string {
  if (row.route) return hashOf(row.route);
  return row.requisition_id ? `#requisitions/${row.requisition_id}` : "#action-center";
}

/**
 * Where a History row leads: its timeline event (DC-47 "loud in the timeline (a Timeline event row)"; home.md call 4
 * "rows are not clickable except their deep link to the audit event"), the requisition's Timeline tab (S18), where the
 * reader may open that requisition; otherwise the screen it was raised on.
 */
export function timelineLinkOf(row: Pick<ActionCenterRow, "route" | "requisition_id" | "requisition_title">): string {
  if (row.requisition_id && row.requisition_title != null) return `#requisitions/${row.requisition_id}/timeline`;
  return row.route ? hashOf(row.route) : "#action-center?tab=history";
}

/** D0's empty value (FIELD_NONE): a History row closed by no person (a system job). Not a word. */
export const NO_CLOSER = "—";

function textOf(row: ActionCenterRow): Pick<ActionRowProps, "verb" | "object" | "reqTitle" | "reqId"> {
  // DC-44 "verb — object": his verb, then the item's server-built title. A mirrored V1 card carries no verb; its title
  // stands alone (D0 gap recorded in reports/S17.md: ActionRow draws the dash and the requisition slot regardless).
  return {
    verb: row.verb ?? row.title,
    object: row.verb ? row.title : "",
    reqTitle: row.requisition_title ?? "",
    reqId: row.requisition_id ?? "",
  };
}

/** D0 ActionRow props for an open row: the kind icon, his verb — the title, the requisition, the SLA clock, the deep link. */
export function openRowProps(row: ActionCenterRow, index: number): ActionRowProps {
  return {
    kind: kindOf(row),
    ...textOf(row),
    href: deepLinkOf(row),
    tier: dueTierOf(row),
    due: row.due_at ?? undefined,
    hs: hsKey("ac.row", index),
  };
}

/** D0 ActionRow props for a History row: *Completed*, "by {closer} · {date/time}", and the link to its timeline event. */
export function historyRowProps(row: ActionCenterRow, index: number): ActionRowProps {
  return {
    kind: kindOf(row),
    ...textOf(row),
    href: timelineLinkOf(row),
    history: { actor: row.closed_by_name || NO_CLOSER, at: row.closed_at ?? row.opened_at },
    hs: hsKey("ac.row", index),
  };
}
